import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { BadgeCheck, CircleAlert, CircleX, Gift, HandCoins, Hourglass, Layers, Percent, RadioTower, ShieldAlert, ShieldCheck, Trophy, Undo2, UserCheck, Wallet } from 'lucide-react'
import { useEffect, useState, type ReactNode } from 'react'

import {
  getReferralQualifications,
  getReferralRewards,
  getReferralSettings,
  patchReferralSettings,
  putReferralDepositTiers,
  reviewReferralQualification,
  reviewReferralReward,
  type AdminReferralQualification,
  type AdminReferralReward,
  type ReferralDepositTier,
  type ReferralDepositTierInput,
  type ReferralRewardKind,
  type ReferralRewardStatus,
  type ReferralSettingsPatch,
} from '../api/admin'
import { PageHeader } from '../components/layout'
import {
  Avatar,
  Badge,
  Button,
  Card,
  EmptyState,
  Field,
  Input,
  Modal,
  Pager,
  Panel,
  Skeleton,
  SkeletonText,
  StatCard,
  Table,
  TableSkeleton,
  Tabs,
  Textarea,
  Toggle,
  Toolbar,
  cx,
  describeError,
  money,
  tableClass,
  ui,
  useToast,
  useTotp,
  when,
  type Tone,
} from '../components/ui/Ui'
import styles from './Referrals.module.css'

type Tab = 'queue' | 'flagged' | 'approved' | 'paid' | 'all' | 'settings'

const TABS: ReadonlyArray<{ value: Tab; label: string }> = [
  { value: 'queue', label: 'На проверке' },
  { value: 'flagged', label: 'Подозрительные' },
  { value: 'approved', label: 'Одобрены' },
  { value: 'paid', label: 'Выплачены' },
  { value: 'all', label: 'Все начисления' },
  { value: 'settings', label: 'Правила и уровни' },
]

const STATUS_OF_TAB: Record<Tab, ReferralRewardStatus | undefined> = {
  queue: 'pending_review',
  flagged: undefined,
  approved: 'approved',
  paid: 'paid',
  all: undefined,
  settings: undefined,
}

const KIND_RU: Record<ReferralRewardKind, string> = {
  fee_rebate: 'доля комиссии',
  signal_grant: 'сигналы',
  promotional_credit: 'промо-бонус',
  deposit_bonus: '% с депозита',
  welcome_bonus: 'приветственный %',
  level_bonus: 'бонус за уровень',
}

const KIND_ICON: Record<ReferralRewardKind, ReactNode> = {
  fee_rebate: <Percent />,
  signal_grant: <RadioTower />,
  promotional_credit: <Gift />,
  deposit_bonus: <HandCoins />,
  welcome_bonus: <Gift />,
  level_bonus: <Trophy />,
}

type Decision = { row: AdminReferralReward; action: 'reject' | 'mark_paid' | 'reverse' }

/** Russian plural: 1 начисление, 2 начисления, 5 начислений. */
function plural(n: number, one: string, few: string, many: string): string {
  const mod10 = n % 10
  const mod100 = n % 100
  if (mod10 === 1 && mod100 !== 11) return one
  if (mod10 >= 2 && mod10 <= 4 && (mod100 < 12 || mod100 > 14)) return few
  return many
}

/** A reward list's first page, the same request the tab makes: its total feeds the overview. */
function useRewardPage(status: ReferralRewardStatus | undefined) {
  return useQuery({ queryKey: ['referral-rewards', status, 1], queryFn: () => getReferralRewards({ page: 1, status }), refetchInterval: 20_000 })
}

/** USDT on a page, only when the page holds the whole list (otherwise the sum would understate it). */
function usdtOf(page: { items: AdminReferralReward[]; total: number } | undefined): string | null {
  if (!page || page.total > page.items.length) return null
  const sum = page.items.filter((row) => row.unit === 'USDT').reduce((total, row) => total + Number(row.amount), 0)
  return sum > 0 ? `${money(String(sum))} USDT` : null
}

/**
 * The partner programme from the operator's side: the reward queue (every
 * cash reward is reviewed and paid explicitly, never auto-credited) and the
 * rules — threshold, grants, levels — that the public terms page shows.
 */
export default function Referrals(): JSX.Element {
  const [tab, setTab] = useState<Tab>('queue')
  const settings = useQuery({ queryKey: ['referral-settings'], queryFn: getReferralSettings })
  const pending = useRewardPage('pending_review')
  const approved = useRewardPage('approved')
  const paid = useRewardPage('paid')
  const flagged = useQuery({ queryKey: ['referral-qualifications', 'flagged'], queryFn: () => getReferralQualifications({ status: 'flagged' }), refetchInterval: 20_000 })
  const program = settings.data?.program
  const flaggedCount = flagged.data?.total

  const kpi = (target: Tab): { onClick: () => void; className?: string } => ({
    onClick: () => setTab(target),
    className: tab === target ? styles.kpiActive : undefined,
  })

  return (
    <>
      <PageHeader
        title="Партнёрская программа"
        description="Каждое денежное начисление партнёру проверяется и выплачивается вручную — само ничего не зачисляется. Правила и уровни здесь — те, что видны на странице условий."
        meta={
          settings.isPending ? (
            <Skeleton width={220} height={20} radius={999} />
          ) : program ? (
            <>
              <Badge tone={program.enabled ? 'accent' : 'warning'} size="sm">
                {program.enabled ? 'программа включена' : 'программа выключена'}
              </Badge>
              <Badge tone="neutral" size="sm" dot={false} mono>
                депозит от {money(program.qualificationDepositUsdt, 0)} {program.qualificationAssetSymbol}
              </Badge>
              {settings.data ? <span className={styles.metaText}>правила изменены {when(settings.data.updatedAt)}</span> : null}
            </>
          ) : null
        }
      />

      <div className={cx('kpi-grid', styles.kpis)}>
        <StatCard
          label="На проверке"
          value={pending.data?.total}
          loading={pending.isPending}
          icon={<Hourglass />}
          tone="warning"
          hint="начисления ждут решения"
          actionLabel="Открыть начисления на проверке"
          {...kpi('queue')}
        />
        <StatCard
          label="Подозрительные"
          value={flaggedCount}
          loading={flagged.isPending}
          icon={<ShieldAlert />}
          tone={flaggedCount !== undefined && flaggedCount > 0 ? 'down' : 'neutral'}
          hint="квалификации ждут оператора"
          actionLabel="Открыть подозрительные квалификации"
          {...kpi('flagged')}
        />
        <StatCard
          label="Одобрены"
          value={approved.data?.total}
          loading={approved.isPending}
          icon={<BadgeCheck />}
          tone="info"
          hint={usdtOf(approved.data) ?? 'денежные ждут выплаты'}
          actionLabel="Открыть одобренные начисления"
          {...kpi('approved')}
        />
        <StatCard
          label="Выплачены"
          value={paid.data?.total}
          loading={paid.isPending}
          icon={<HandCoins />}
          tone="accent"
          hint={usdtOf(paid.data) ?? 'выплата записана вручную'}
          actionLabel="Открыть выплаченные начисления"
          {...kpi('paid')}
        />
      </div>

      <Toolbar>
        <Tabs value={tab} options={TABS} onChange={setTab} ariaLabel="Разделы программы" />
      </Toolbar>
      <div key={tab} className={styles.tabBody}>
        {tab === 'settings' ? <Settings /> : tab === 'flagged' ? <Flagged /> : <Rewards status={STATUS_OF_TAB[tab]} />}
      </div>
    </>
  )
}

const FLAG_RU: Record<string, string> = {
  same_device_as_referrer: 'то же устройство, что у приглашающего',
  same_ip_as_referrer: 'тот же адрес, что у приглашающего',
  device_shared_with_team: 'устройство уже есть в команде',
  ip_shared_with_team: 'адрес уже есть в команде',
}

/** An email with its initials avatar, for the people columns. */
function Person({ email, quiet = false }: { email: string; quiet?: boolean }): JSX.Element {
  return (
    <span className={cx(styles.person, quiet ? styles.personQuiet : '')}>
      <Avatar name={email} size={24} />
      <span className={styles.personEmail}>{email}</span>
    </span>
  )
}

/**
 * Qualifications the anti-abuse rules held: the referred account registered
 * from the referrer's device or address, or from one already in the team.
 * Nothing was granted; approve hands out every award, reject revokes.
 */
function Flagged(): JSX.Element {
  const toast = useToast()
  const withTotp = useTotp()
  const queryClient = useQueryClient()
  const [decision, setDecision] = useState<{ row: AdminReferralQualification; action: 'approve' | 'reject' } | null>(null)
  const [reason, setReason] = useState('')
  const list = useQuery({ queryKey: ['referral-qualifications', 'flagged'], queryFn: () => getReferralQualifications({ status: 'flagged' }), refetchInterval: 20_000 })

  const review = useMutation({
    mutationFn: (input: { id: string; action: 'approve' | 'reject'; reason: string }) =>
      withTotp((code) => reviewReferralQualification(input.id, { decision: input.action, reason: input.reason }, code)),
    onSuccess: (updated) => {
      if (updated === null) return
      toast(updated.status === 'qualified' ? `${updated.referredEmail} — квалификация подтверждена` : `${updated.referredEmail} — отклонено`)
      setDecision(null)
      setReason('')
      void queryClient.invalidateQueries({ queryKey: ['referral-qualifications'] })
    },
    onError: (error) => toast(describeError(error), true),
  })

  const rows = list.data?.items ?? []
  return (
    <>
      <Panel>
        {list.isError ? (
          <EmptyState tone="error" icon={<CircleAlert />} title="Список не загрузился" description={describeError(list.error)} />
        ) : list.isPending ? (
          <TableSkeleton rows={3} headers={['Депозит', 'Приглашённый', 'Приглашающий', 'Сумма', 'Признаки', '']} />
        ) : rows.length === 0 ? (
          <EmptyState icon={<ShieldCheck />} tone="success" title="Подозрительных квалификаций нет" description="Сюда попадают квалификации, которые остановила проверка на злоупотребления." />
        ) : (
          <Table ariaLabel="Подозрительные квалификации">
            <thead>
              <tr>
                <th>Депозит</th>
                <th>Приглашённый</th>
                <th>Приглашающий</th>
                <th className={tableClass.right}>Сумма</th>
                <th>Признаки</th>
                <th>
                  <span className="sr-only">Действия</span>
                </th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <tr key={row.id}>
                  <td className={cx(tableClass.muted, tableClass.nowrap)}>{when(row.qualifiedAt)}</td>
                  <td>
                    <Person email={row.referredEmail} />
                  </td>
                  <td>
                    <Person email={row.referrerEmail} quiet />
                  </td>
                  <td className={tableClass.right}>
                    <span className="num">{money(row.qualifyingAmountUsdt)}</span> <span className={styles.unit}>USDT</span>
                  </td>
                  <td className={tableClass.wrap}>
                    <div className={styles.flags}>
                      {row.riskFlags.map((flag) => (
                        <Badge key={flag} tone="warning" size="sm">
                          {FLAG_RU[flag] ?? flag}
                        </Badge>
                      ))}
                    </div>
                  </td>
                  <td className={tableClass.actions}>
                    <div className={styles.rowActions}>
                      <Button size="sm" variant="primary" onClick={() => setDecision({ row, action: 'approve' })}>
                        Подтвердить
                      </Button>
                      <Button size="sm" variant="danger" onClick={() => setDecision({ row, action: 'reject' })}>
                        Отклонить
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        )}
      </Panel>
      <Modal
        open={decision !== null}
        onClose={() => setDecision(null)}
        title={decision?.action === 'approve' ? 'Подтвердить квалификацию' : 'Отклонить квалификацию'}
        icon={decision?.action === 'approve' ? <UserCheck /> : <CircleX />}
        tone={decision?.action === 'approve' ? 'accent' : 'down'}
        subtitle={decision === null ? undefined : `${decision.row.referredEmail} ← ${decision.row.referrerEmail}`}
        footer={
          <>
            <Button variant="ghost" onClick={() => setDecision(null)}>
              Отмена
            </Button>
            <Button
              variant={decision?.action === 'approve' ? 'primary' : 'danger-solid'}
              disabled={reason.trim().length < 2}
              loading={review.isPending}
              onClick={() => decision && review.mutate({ id: decision.row.id, action: decision.action, reason: reason.trim() })}
            >
              {decision?.action === 'approve' ? 'Подтвердить и выдать бонусы' : 'Отклонить'}
            </Button>
          </>
        }
      >
        <Field label="Причина" hint="Попадёт в аудит и в историю квалификации.">
          <Textarea value={reason} onChange={(event) => setReason(event.target.value)} />
        </Field>
      </Modal>
    </>
  )
}

const EMPTY_OF_STATUS: Record<string, { title: string; description: string }> = {
  pending_review: { title: 'Начислений на проверке нет', description: 'Очередь пуста — решать сейчас нечего.' },
  approved: { title: 'Одобренных начислений нет', description: 'Здесь ждут выплаты одобренные начисления.' },
  paid: { title: 'Выплат пока не было', description: 'Сюда попадают начисления, выплату которых записал оператор.' },
  all: { title: 'Начислений нет', description: 'Начисления партнёрам появятся здесь.' },
}

function Rewards({ status }: { status: ReferralRewardStatus | undefined }): JSX.Element {
  const [page, setPage] = useState(1)
  const [decision, setDecision] = useState<Decision | null>(null)
  const [reason, setReason] = useState('')
  const [reference, setReference] = useState('')
  const queryClient = useQueryClient()
  const toast = useToast()
  const withTotp = useTotp()

  const list = useQuery({ queryKey: ['referral-rewards', status, page], queryFn: () => getReferralRewards({ page, status }), refetchInterval: 20_000 })

  const review = useMutation({
    mutationFn: ({ row, action, code }: { row: AdminReferralReward; action: 'approve' | Decision['action']; code: string }) =>
      reviewReferralReward(
        row.id,
        {
          action,
          ...(action === 'reject' || action === 'reverse' ? { reason: reason.trim() } : {}),
          ...(action === 'mark_paid' ? { payoutReference: reference.trim() } : {}),
        },
        code,
      ),
    onSuccess: (updated) => {
      toast(`${updated.beneficiaryEmail} — ${STATUS_RU[updated.status]}`)
      setDecision(null)
      setReason('')
      setReference('')
      void queryClient.invalidateQueries({ queryKey: ['referral-rewards'] })
    },
    onError: (error) => toast(describeError(error), true),
  })

  const run = (row: AdminReferralReward, action: 'approve' | Decision['action']): void => {
    void withTotp((code) => review.mutateAsync({ row, action, code }))
  }
  const rows = list.data?.items ?? []
  const empty = EMPTY_OF_STATUS[status ?? 'all'] ?? EMPTY_OF_STATUS.all

  return (
    <>
      <Panel>
        {list.isError ? (
          <EmptyState tone="error" icon={<CircleAlert />} title="Список не загрузился" description={describeError(list.error)} />
        ) : list.isPending ? (
          <TableSkeleton rows={4} headers={['Начислено', 'Партнёр', 'За кого', 'Вид', 'Сумма', 'Основание', 'Статус', '']} />
        ) : rows.length === 0 ? (
          <EmptyState icon={status === 'paid' ? <HandCoins /> : status === 'approved' ? <BadgeCheck /> : <Hourglass />} title={empty.title} description={empty.description} />
        ) : (
          <Table ariaLabel="Начисления партнёрам">
            <thead>
              <tr>
                <th>Начислено</th>
                <th>Партнёр</th>
                <th>За кого</th>
                <th>Вид</th>
                <th className={tableClass.right}>Сумма</th>
                <th>Основание</th>
                <th>Статус</th>
                <th>
                  <span className="sr-only">Действия</span>
                </th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <tr key={row.id}>
                  <td className={cx(tableClass.muted, tableClass.nowrap)}>{when(row.createdAt)}</td>
                  <td>
                    <Person email={row.beneficiaryEmail} />
                  </td>
                  <td className={tableClass.muted}>{row.relatedUserEmail ?? '—'}</td>
                  <td>
                    <span className={styles.kind}>
                      {KIND_ICON[row.kind]}
                      {KIND_RU[row.kind]}
                    </span>
                  </td>
                  <td className={cx(tableClass.right, tableClass.nowrap)}>
                    {row.unit === 'signal' ? (
                      <>
                        <span className="num">{money(row.amount, 0)}</span> <span className={styles.unit}>сигн.</span>
                      </>
                    ) : (
                      <>
                        <span className="num">{money(row.amount)}</span> <span className={styles.unit}>USDT</span>
                      </>
                    )}
                  </td>
                  <td className={cx(tableClass.wrap, tableClass.muted)}>
                    {row.kind === 'fee_rebate' && row.basisAmountUsdt
                      ? `${money(row.feeShareRate === null ? '0' : String(Number(row.feeShareRate) * 100), 0)} % от комиссии ${money(row.basisAmountUsdt, 4)} USDT`
                      : row.description}
                    {row.payout?.externalReference ? <span className={cx(tableClass.sub, 'num')}>ref {row.payout.externalReference}</span> : null}
                    {row.reviewReason ? <span className={tableClass.sub}>{row.reviewReason}</span> : null}
                  </td>
                  <td>
                    <Badge tone={TONE[row.status]} size="sm">
                      {STATUS_RU[row.status]}
                    </Badge>
                  </td>
                  <td className={tableClass.actions}>
                    <div className={styles.rowActions}>
                      {row.status === 'pending_review' ? (
                        <>
                          <Button size="sm" variant="primary" onClick={() => run(row, 'approve')}>
                            Одобрить
                          </Button>
                          <Button size="sm" variant="danger" onClick={() => setDecision({ row, action: 'reject' })}>
                            Отклонить
                          </Button>
                        </>
                      ) : null}
                      {row.status === 'approved' && row.unit === 'USDT' ? (
                        <Button size="sm" variant="primary" onClick={() => setDecision({ row, action: 'mark_paid' })}>
                          Выплачено
                        </Button>
                      ) : null}
                      {row.status === 'approved' || row.status === 'paid' ? (
                        <Button size="sm" variant="danger" onClick={() => setDecision({ row, action: 'reverse' })}>
                          Отозвать
                        </Button>
                      ) : null}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        )}
        {list.data ? <Pager page={page} pageSize={list.data.pageSize} total={list.data.total} onPage={setPage} /> : null}
      </Panel>

      <Modal
        open={decision !== null}
        onClose={() => setDecision(null)}
        title={decision?.action === 'mark_paid' ? 'Отметить выплату' : decision?.action === 'reject' ? 'Отклонить начисление' : 'Отозвать начисление'}
        icon={decision?.action === 'mark_paid' ? <HandCoins /> : decision?.action === 'reject' ? <CircleX /> : <Undo2 />}
        tone={decision?.action === 'mark_paid' ? 'accent' : 'down'}
        subtitle={decision ? `${decision.row.beneficiaryEmail} · ${decision.row.unit === 'signal' ? `${decision.row.amount} сигн.` : `${money(decision.row.amount)} USDT`} · ${KIND_RU[decision.row.kind]}` : undefined}
        footer={
          <>
            <Button variant="ghost" onClick={() => setDecision(null)}>
              Отмена
            </Button>
            <Button
              variant={decision?.action === 'mark_paid' ? 'primary' : 'danger-solid'}
              loading={review.isPending}
              disabled={decision?.action === 'mark_paid' ? reference.trim().length < 3 : reason.trim().length < 2}
              onClick={() => decision && run(decision.row, decision.action)}
            >
              {decision?.action === 'mark_paid' ? 'Подтвердить выплату' : decision?.action === 'reject' ? 'Отклонить' : 'Отозвать'}
            </Button>
          </>
        }
      >
        {decision?.action === 'mark_paid' ? (
          <Field label="Ссылка на выплату" hint="Хеш транзакции или номер платежа. Панель ничего не переводит сама: она записывает, что деньги ушли.">
            <Input mono value={reference} onChange={(event) => setReference(event.target.value)} />
          </Field>
        ) : (
          <Field label="Причина" hint="Попадёт в историю начисления и в аудит.">
            <Textarea value={reason} onChange={(event) => setReason(event.target.value)} />
          </Field>
        )}
      </Modal>
    </>
  )
}

const STATUS_RU: Record<ReferralRewardStatus, string> = {
  pending_review: 'на проверке',
  approved: 'одобрено',
  paid: 'выплачено',
  rejected: 'отклонено',
  reversed: 'отозвано',
}
const TONE: Record<ReferralRewardStatus, Tone> = {
  pending_review: 'warning',
  approved: 'accent',
  paid: 'accent',
  rejected: 'down',
  reversed: 'down',
}

function Settings(): JSX.Element {
  const settings = useQuery({ queryKey: ['referral-settings'], queryFn: getReferralSettings })
  const [form, setForm] = useState<ReferralSettingsPatch>({})
  const queryClient = useQueryClient()
  const toast = useToast()
  const withTotp = useTotp()

  useEffect(() => {
    if (settings.data) {
      const { rebateBasis: _basis, ...program } = settings.data.program
      setForm(program)
    }
  }, [settings.data])

  const save = useMutation({
    mutationFn: (code: string) => {
      const current = settings.data?.program
      const changed: ReferralSettingsPatch = {}
      for (const key of Object.keys(form) as Array<keyof ReferralSettingsPatch>) {
        if (current === undefined || String(form[key]) !== String(current[key])) (changed as Record<string, unknown>)[key] = form[key]
      }
      if (Object.keys(changed).length === 0) return Promise.reject(new Error('Ничего не изменилось'))
      return patchReferralSettings(changed, code)
    },
    onSuccess: () => {
      toast('Правила сохранены и записаны в аудит')
      void queryClient.invalidateQueries({ queryKey: ['referral-settings'] })
    },
    onError: (error) => toast(describeError(error), true),
  })

  if (settings.isPending) {
    return (
      <div className={styles.rules}>
        <Panel className={styles.loadingPanel}>
          <SkeletonText lines={6} />
        </Panel>
        <Panel className={styles.loadingPanel}>
          <SkeletonText lines={4} />
        </Panel>
      </div>
    )
  }
  if (settings.isError) {
    return (
      <Panel>
        <EmptyState tone="error" icon={<CircleAlert />} title="Правила не загрузились" description={describeError(settings.error)} />
      </Panel>
    )
  }
  const set = <K extends keyof ReferralSettingsPatch>(key: K, value: ReferralSettingsPatch[K]): void => setForm((current) => ({ ...current, [key]: value }))
  // What «Сохранить правила» would send: the same comparison the save makes.
  const current = settings.data.program
  const changedCount = (Object.keys(form) as Array<keyof ReferralSettingsPatch>).filter((key) => String(form[key]) !== String(current[key])).length
  const secondLevelPercent = Number(form.secondLevelShareRate ?? '')
  const sharePercent = Number(form.qualificationMinReferrerBalanceShare ?? '')
  const shareHint =
    Number.isFinite(sharePercent) && (form.qualificationMinReferrerBalanceShare ?? '') !== ''
      ? `= ${money(String(sharePercent * 100), 0)} %`
      : undefined

  return (
    <div className={styles.rules}>
      <div className={styles.rulesMain}>
        <Card icon={<UserCheck />} title="Квалификация и права" description="Когда приглашённый считается квалифицированным и что за это получают обе стороны.">
          <div className={styles.toggles}>
            <Toggle switchLast className={styles.toggleRow} checked={form.enabled ?? true} label="Программа включена" onChange={(next) => set('enabled', next)} />
            <Toggle switchLast className={styles.toggleRow} checked={form.qualificationRequiresKyc ?? false} label="Нужна верификация" description="Квалификация засчитывается только верифицированному приглашённому." onChange={(next) => set('qualificationRequiresKyc', next)} />
          </div>
          <div className={ui.grid2}>
            <Field label="Квалифицирующий депозит" hint="Первый зачисленный депозит приглашённого не меньше этой суммы.">
              <Input mono value={form.qualificationDepositUsdt ?? ''} onChange={(event) => set('qualificationDepositUsdt', event.target.value)} trailing="USDT" />
            </Field>
            <Field label="Актив депозита">
              <Input mono value={form.qualificationAssetSymbol ?? ''} onChange={(event) => set('qualificationAssetSymbol', event.target.value.toUpperCase())} />
            </Field>
            <Field label="Бонусных сигналов новичку" hint="Выдаются правом на 14 дней после квалификации.">
              <Input mono type="number" min={0} max={100} value={form.welcomeSignalGrants ?? 0} onChange={(event) => set('welcomeSignalGrants', Number(event.target.value))} />
            </Field>
            <Field label="Бонусных сигналов приглашающему">
              <Input mono type="number" min={0} max={100} value={form.referrerSignalGrants ?? 0} onChange={(event) => set('referrerSignalGrants', Number(event.target.value))} />
            </Field>
            <Field
              label="Доля от баланса приглашающего"
              hint="Депозит должен быть ещё и не меньше этой доли баланса приглашающего. 0.3 = 30 %, 0 — правило выключено."
              aside={shareHint}
            >
              <Input mono value={form.qualificationMinReferrerBalanceShare ?? ''} onChange={(event) => set('qualificationMinReferrerBalanceShare', event.target.value)} />
            </Field>
            <Field label="Постоянный премиум-сигнал" hint="С какого числа квалифицированных приглашённых открывается премиум-доступ. 0 — не открывать.">
              <Input mono type="number" min={0} max={100} value={form.premiumSignalMinDirects ?? 0} onChange={(event) => set('premiumSignalMinDirects', Number(event.target.value))} trailing="приглашённых" />
            </Field>
          </div>
        </Card>

        <Card icon={<Wallet />} title="Деньги" description="Промо-бонус новичку, удержание депозита и доли комиссии партнёрам.">
          <div className={ui.grid2}>
            <Field label="Промо-бонус новичку" hint="0 — без бонуса. Замораживается при квалификации и сам ложится на спот, когда выполнено условие по обороту.">
              <Input mono value={form.promotionalRewardUsdt ?? ''} onChange={(event) => set('promotionalRewardUsdt', event.target.value)} trailing="USDT" />
            </Field>
            <Field label="Условие: оборот × бонус" hint="10 — бонус 30 USDT разблокируется после 300 USDT сделок. 0 — сразу.">
              <Input mono value={form.promoTurnoverMultiple ?? ''} onChange={(event) => set('promoTurnoverMultiple', event.target.value)} />
            </Field>
            <Field label="Срок бонуса" hint="Не выполнил условие — бонус сгорает.">
              <Input mono type="number" min={1} max={365} value={form.promoValidDays ?? 30} onChange={(event) => set('promoValidDays', Number(event.target.value))} trailing="дней" />
            </Field>
            <Field label="Удержание депозита" hint="Заявка на вывод раньше — квалификация и бонус отменяются. 0 — без удержания.">
              <Input mono type="number" min={0} max={180} value={form.qualificationHoldDays ?? 14} onChange={(event) => set('qualificationHoldDays', Number(event.target.value))} trailing="дней" />
            </Field>
            <Field label="Окно повторного депозита" hint="Депозит в этот срок складывается с первым, пара пересчитывается по таблице, разница доплачивается — один раз. 0 — выключено.">
              <Input mono type="number" min={0} max={90} value={form.topupWindowDays ?? 7} onChange={(event) => set('topupWindowDays', Number(event.target.value))} trailing="дней" />
            </Field>
            <Field
              label="Второй уровень: доля комиссии"
              hint="Доля той же удержанной комиссии партнёру партнёра (0.02 = 2 %). 0 — уровень выключен."
              aside={Number.isFinite(secondLevelPercent) && (form.secondLevelShareRate ?? '') !== '' ? `= ${money(String(secondLevelPercent * 100), 0)} %` : undefined}
            >
              <Input mono value={form.secondLevelShareRate ?? ''} onChange={(event) => set('secondLevelShareRate', event.target.value)} />
            </Field>
          </div>
          <div className={cx(styles.toggles, styles.togglesAfter)}>
            <Toggle
              switchLast
              className={styles.toggleRow}
              checked={form.antiabuseEnabled ?? true}
              label="Проверка на злоупотребления"
              description="Совпадение устройства или адреса с приглашающим или его командой останавливает квалификацию до решения оператора."
              onChange={(next) => set('antiabuseEnabled', next)}
            />
            <Toggle
              switchLast
              className={styles.toggleRow}
              checked={form.feeRebatesEnabled ?? true}
              label="Доля комиссии партнёру"
              description="Считается от комиссии, которую платформа реально удержала. Сейчас комиссии нулевые — доля будет нулевой."
              onChange={(next) => set('feeRebatesEnabled', next)}
            />
            <Toggle
              switchLast
              className={styles.toggleRow}
              checked={form.depositTiersEnabled ?? true}
              label="Проценты с депозита по таблице"
              description="Включено — приглашающий и новичок получают процент от квалифицирующего депозита по таблице ниже. Выключено — новичку платится только фиксированный промо-бонус."
              onChange={(next) => set('depositTiersEnabled', next)}
            />
            <Toggle
              switchLast
              className={styles.toggleRow}
              checked={form.promotionBonusesEnabled ?? true}
              label="Разовый бонус за уровень"
              description="Начисляется один раз при первом достижении уровня VIP."
              onChange={(next) => set('promotionBonusesEnabled', next)}
            />
            <Toggle
              switchLast
              className={styles.toggleRow}
              checked={form.rewardReviewRequired ?? true}
              label="Проверять каждое начисление"
              description="Выключено — доли комиссии сразу «одобрены», выплата всё равно вручную."
              onChange={(next) => set('rewardReviewRequired', next)}
            />
          </div>
        </Card>

        <div className={styles.saveBar}>
          <Button variant="primary" loading={save.isPending} onClick={() => void withTotp((code) => save.mutateAsync(code))}>
            Сохранить правила
          </Button>
          <span className={styles.saveNote}>
            {changedCount > 0 ? (
              <span className={styles.dirty}>
                {changedCount} {plural(changedCount, 'поле изменено', 'поля изменены', 'полей изменено')}, не сохранено
              </span>
            ) : (
              'Изменений нет'
            )}
            <span className={styles.saveMeta}>· изменено {when(settings.data.updatedAt)}</span>
          </span>
        </div>
      </div>

      <div className={styles.rulesSide}>
        <Card
          icon={<Layers />}
          title="Уровни партнёров"
          description="Уровень — то, что выше: по собственным депозитам или по приглашённым с квалифицирующим депозитом. Сигналы внутри уровня не ограничены."
          flush
          footer={
            <span className={styles.cardNote}>
              Уровень пересчитывается при каждой квалификации и отзыве. Ставки уровней меняются миграцией: это часть публичных условий, а не настройка смены.
            </span>
          }
        >
          <Table dense stickyHeader={false} ariaLabel="Уровни партнёров">
            <thead>
              <tr>
                <th>Уровень</th>
                <th className={tableClass.right} title="Приглашённых с квалифицирующим депозитом">
                  Прямых
                </th>
                <th className={tableClass.right} title="Собственные депозиты, которые открывают уровень сами по себе">
                  Депозит
                </th>
                <th className={tableClass.right}>Доля</th>
                <th className={tableClass.right} title="Разовый бонус при первом достижении уровня">
                  Бонус
                </th>
              </tr>
            </thead>
            <tbody>
              {settings.data.levels.map((level, index) => (
                <tr key={level.id}>
                  <td>
                    <span className={styles.level}>
                      <span className={styles.rank} data-rank={Math.min(index, 4)}>
                        {level.rank}
                      </span>
                      <span className={styles.levelName} title={level.code}>
                        {level.name}
                      </span>
                    </span>
                  </td>
                  <td className={cx(tableClass.right, 'num')}>
                    {level.minQualifiedDirects === null ? '—' : `от ${level.minQualifiedDirects}`}
                  </td>
                  <td className={cx(tableClass.right, 'num')}>
                    {level.minDepositUsdt === null ? '—' : `от ${money(level.minDepositUsdt, 0)} $`}
                  </td>
                  <td className={cx(tableClass.right, 'num')}>{money(String(Number(level.feeShareRate) * 100), 0)} %</td>
                  <td className={cx(tableClass.right, 'num')}>
                    {Number(level.promotionBonusUsdt) > 0 ? `${money(level.promotionBonusUsdt, 0)} $` : '—'}
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        </Card>
        <TopPartners />
      </div>
      <DepositTiers tiers={settings.data.depositTiers} enabled={current.depositTiersEnabled} />
    </div>
  )
}

/** Reads the table back the way a user would: one amount, both percentages. */
/** A rate the operator is still typing is not a number yet: show nothing. */
function percentOf(rate: string): string {
  const percent = Number(rate) * 100
  return Number.isFinite(percent) ? `${money(String(percent), 0)} %` : '—'
}

function exampleOf(rows: readonly ReferralDepositTierInput[], amount: number): string {
  const band = rows.find(
    (row) => Number(row.minDepositUsdt) <= amount && (row.maxDepositUsdt === null || Number(row.maxDepositUsdt) > amount),
  )
  if (band === undefined) return `пример: депозит ${money(String(amount), 0)} — вне таблицы, начислений нет`
  const referrer = amount * Number(band.referrerRate)
  const welcome = amount * Number(band.welcomeRate)
  if (!Number.isFinite(referrer) || !Number.isFinite(welcome)) return 'пример появится, когда проценты заполнены'
  return `пример: депозит ${money(String(amount), 0)} → приглашающему ${money(String(referrer), 0)} USDT, новичку ${money(String(welcome), 0)} USDT`
}

/**
 * The deposit table: what the inviter and the newcomer get, as a share of the
 * qualifying deposit, by how large that deposit is. Bands are half-open —
 * one band's upper bound is the next one's lower bound — so the editor keeps
 * them chained and only lets the last one stay open-ended.
 */
function DepositTiers({ tiers, enabled }: { tiers: ReferralDepositTier[]; enabled: boolean }): JSX.Element {
  const [rows, setRows] = useState<ReferralDepositTierInput[]>([])
  const queryClient = useQueryClient()
  const toast = useToast()
  const withTotp = useTotp()

  useEffect(() => {
    setRows(tiers.map(({ name, minDepositUsdt, maxDepositUsdt, referrerRate, welcomeRate }) => ({ name, minDepositUsdt, maxDepositUsdt, referrerRate, welcomeRate })))
  }, [tiers])

  const save = useMutation({
    mutationFn: (code: string) => putReferralDepositTiers(rows, code),
    onSuccess: () => {
      toast('Таблица процентов сохранена и записана в аудит')
      void queryClient.invalidateQueries({ queryKey: ['referral-settings'] })
    },
    onError: (error) => toast(describeError(error), true),
  })

  const edit = (index: number, patch: Partial<ReferralDepositTierInput>): void =>
    setRows((current) => current.map((row, position) => (position === index ? { ...row, ...patch } : row)))

  // Bands chain: editing an upper bound moves the next lower bound with it.
  const editMax = (index: number, value: string): void =>
    setRows((current) =>
      current.map((row, position) => {
        if (position === index) return { ...row, maxDepositUsdt: value }
        if (position === index + 1) return { ...row, minDepositUsdt: value }
        return row
      }),
    )

  const add = (): void =>
    setRows((current) => {
      const last = current.at(-1)
      const boundary = last === undefined ? '500' : String(Number(last.minDepositUsdt) * 2)
      const closed = current.map((row, position) => (position === current.length - 1 ? { ...row, maxDepositUsdt: boundary } : row))
      return [...closed, { name: 'Новый диапазон', minDepositUsdt: boundary, maxDepositUsdt: null, referrerRate: '0', welcomeRate: '0' }]
    })

  const remove = (index: number): void =>
    setRows((current) => {
      const next = current.filter((_row, position) => position !== index)
      // Whatever is left, the last band is the open-ended one.
      return next.map((row, position) => (position === next.length - 1 ? { ...row, maxDepositUsdt: null } : row))
    })

  const dirty = JSON.stringify(rows) !== JSON.stringify(tiers.map(({ name, minDepositUsdt, maxDepositUsdt, referrerRate, welcomeRate }) => ({ name, minDepositUsdt, maxDepositUsdt, referrerRate, welcomeRate })))

  return (
    <Card
      className={styles.tiersCard}
      icon={<HandCoins />}
      title="Проценты по сумме депозита"
      description="Процент приглашающему и приветственный процент новичку от первого квалифицирующего депозита. Диапазон включает нижнюю границу и не включает верхнюю, у последнего верхней границы нет."
      footer={
        <span className={styles.cardNote}>
          {enabled
            ? 'Начисления попадают в очередь на проверку и выплачиваются вручную — ничего не зачисляется само.'
            : 'Проценты с депозита сейчас выключены: таблицу можно подготовить заранее, начисления по ней не идут.'}
        </span>
      }
    >
      <Table dense stickyHeader={false} ariaLabel="Проценты по сумме депозита">
        <thead>
          <tr>
            <th>Диапазон</th>
            <th className={tableClass.right}>От, USDT</th>
            <th className={tableClass.right}>До, USDT</th>
            <th className={tableClass.right} title="Доля депозита приглашающему">
              Приглашающему
            </th>
            <th className={tableClass.right} title="Доля депозита новичку">
              Новичку
            </th>
            <th />
          </tr>
        </thead>
        <tbody>
          {rows.map((row, index) => (
            <tr key={index}>
              <td>
                <Input value={row.name} onChange={(event) => edit(index, { name: event.target.value })} />
              </td>
              <td className={tableClass.right}>
                <Input mono value={row.minDepositUsdt} disabled={index > 0} onChange={(event) => edit(index, { minDepositUsdt: event.target.value })} />
              </td>
              <td className={tableClass.right}>
                {index === rows.length - 1 ? (
                  <span className={styles.openBand}>без границы</span>
                ) : (
                  <Input mono value={row.maxDepositUsdt ?? ''} onChange={(event) => editMax(index, event.target.value)} />
                )}
              </td>
              <td className={tableClass.right}>
                <Input mono value={row.referrerRate} onChange={(event) => edit(index, { referrerRate: event.target.value })} trailing={percentOf(row.referrerRate)} />
              </td>
              <td className={tableClass.right}>
                <Input mono value={row.welcomeRate} onChange={(event) => edit(index, { welcomeRate: event.target.value })} trailing={percentOf(row.welcomeRate)} />
              </td>
              <td className={tableClass.right}>
                <Button variant="ghost" size="sm" disabled={rows.length <= 1} onClick={() => remove(index)}>
                  Удалить
                </Button>
              </td>
            </tr>
          ))}
        </tbody>
      </Table>
      <div className={styles.saveBar}>
        <Button variant="primary" loading={save.isPending} disabled={!dirty} onClick={() => void withTotp((code) => save.mutateAsync(code))}>
          Сохранить таблицу
        </Button>
        <Button variant="ghost" onClick={add}>
          Добавить диапазон
        </Button>
        <span className={styles.saveNote}>
          {dirty ? <span className={styles.dirty}>не сохранено</span> : 'Изменений нет'}
          <span className={styles.saveMeta}> · {exampleOf(rows, 5000)}</span>
        </span>
      </div>
    </Card>
  )
}

/**
 * Who earned the most among the latest rewards (the first page of «Все
 * начисления»): rejected and reversed ones do not count.
 */
function TopPartners(): JSX.Element {
  const list = useQuery({ queryKey: ['referral-rewards', undefined, 1], queryFn: () => getReferralRewards({ page: 1, status: undefined }), refetchInterval: 20_000 })
  const byPartner = new Map<string, { email: string; usdt: number; signals: number; count: number }>()
  for (const row of list.data?.items ?? []) {
    if (row.status === 'rejected' || row.status === 'reversed') continue
    const entry = byPartner.get(row.beneficiaryUserId) ?? { email: row.beneficiaryEmail, usdt: 0, signals: 0, count: 0 }
    entry.count += 1
    if (row.unit === 'USDT') entry.usdt += Number(row.amount)
    else entry.signals += Number(row.amount)
    byPartner.set(row.beneficiaryUserId, entry)
  }
  const top = [...byPartner.values()].sort((a, b) => b.usdt - a.usdt || b.signals - a.signals || b.count - a.count).slice(0, 5)
  const pageSize = list.data?.pageSize ?? 50

  return (
    <Card icon={<Trophy />} title="Партнёры с наибольшими начислениями" description={`По последним ${pageSize} начислениям, без отклонённых и отозванных.`} flush>
      {list.isError ? (
        <EmptyState compact tone="error" icon={<CircleAlert />} title="Не загрузилось" description={describeError(list.error)} />
      ) : list.isPending ? (
        <div className={styles.loadingPanel}>
          <SkeletonText lines={3} />
        </div>
      ) : top.length === 0 ? (
        <EmptyState compact icon={<Trophy />} title="Начислений ещё не было" description="Партнёры появятся здесь после первых начислений." />
      ) : (
        <ol className={cx(styles.top, 'stagger')}>
          {top.map((partner, index) => (
            <li key={partner.email} className={styles.topRow}>
              <span className={styles.topPlace}>{index + 1}</span>
              <Avatar name={partner.email} size={28} />
              <span className={styles.topWho}>
                <span className={styles.personEmail}>{partner.email}</span>
                <span className={styles.topSub}>
                  {partner.count} {plural(partner.count, 'начисление', 'начисления', 'начислений')}
                </span>
              </span>
              <span className={styles.topFigures}>
                {partner.usdt > 0 || partner.signals === 0 ? (
                  <>
                    <span className="num">{money(String(partner.usdt))} USDT</span>
                    {partner.signals > 0 ? <span className={cx(styles.topSub, 'num')}>+ {money(String(partner.signals), 0)} сигн.</span> : null}
                  </>
                ) : (
                  <span className="num">{money(String(partner.signals), 0)} сигн.</span>
                )}
              </span>
            </li>
          ))}
        </ol>
      )}
    </Card>
  )
}
